/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * SRAM text area (CRCB2B-633). No size axis — the design defines one box.
 *
 * Figma's 100px is applied as a *minimum*, not a fixed height: comment volume
 * varies by implementation, so call sites stay free to set a taller box via
 * `className`. `field-sizing-content` is kept so the control also grows with
 * its content above that floor.
 *
 * Shares the Text Input treatment — --muted fill, 1px --border, 16px/1.5/3%
 * type — including its known WCAG SC 1.4.11 shortfall. See `input.tsx`.
 */
function Textarea({ className, ...props }: React.ComponentProps<'textarea'>) {
    return (
        <textarea
            data-slot="textarea"
            className={cn(
                'border-input text-input-foreground placeholder:text-placeholder flex field-sizing-content min-h-25 w-full rounded-ui border bg-muted px-4 py-3 text-base leading-[1.5] tracking-[0.03em] shadow-ui transition-[color,box-shadow] outline-none',
                'hover:border-border-hover',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                // Border-only invalid treatment, matching Input — Figma's Invalid
                // variant recolors the boundary and drops the upstream ring tint.
                'aria-invalid:border-border-error',
                // Disabled recolors rather than fades, and keeps the resting --muted
                // fill rather than Figma's undeclared (white) one; see input.tsx.
                'disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-border-disabled disabled:text-disabled-foreground disabled:placeholder:text-disabled-foreground',
                // Readonly keeps the 100px floor but loses the box, per Figma
                // 2286:20463 — unlike the Text Input, which collapses to content.
                '[&[readonly]]:border-transparent [&[readonly]]:border-b-border-subtle [&[readonly]]:bg-transparent [&[readonly]]:px-0 [&[readonly]]:[--ui-radius:0] [&[readonly]]:[--ui-shadow:0_0_#0000]',
                className
            )}
            {...props}
        />
    );
}

export { Textarea };
